import { timecodeAt, timecodeSpec, type FramerateName } from "@infoflyt/shared/timecode";
import { supabase } from "./supabase";
import * as db from "./db";
import { erEkteProduksjon } from "./session";
import type { Deltaker, Spoersmaalskilde, SuSpoersmaal } from "./types";

/**
 * Deltakere og SU-/synkspoersmaal: henting i feltappen.
 *
 * SU er stand up — deltakeren staar i gangen og svarer paa tre raske
 * spoersmaal. En SYNK er det lange intervjuet. To situasjoner, samme behov:
 * en liste over hva som skal spoerres om. Se docs/01-domene.md.
 *
 * BEGGE LISTENE HOERER TIL PRODUKSJONEN. Redaktoer skriver spoersmaalene fra
 * nettflata og setter opp deltakerne med forkortelser; regi legger til i
 * appen naar noe dukker opp i felt. Det er motsatt av hurtigknappene, som er
 * per bruker — knappene er ergonomi, spoersmaalene er redaksjonelt innhold.
 *
 * OFFLINE FOERST, som dokumentene. `lesLokalt` svarer fra disk uten aa roere
 * nettet: en synk tas der den tas, og det er sjelden der det er dekning.
 * `synkroniser` kjoeres etterpaa.
 */

export interface SuData {
  readonly deltakere: readonly Deltaker[];
  readonly spoersmaal: readonly SuSpoersmaal[];
  /**
   * Parene «spoersmaal·deltaker» som er stilt ET STED i produksjonen.
   *
   * Et spoersmaal som er stilt er stilt for alle — avgjort 25.10.2026. Her
   * ligger ogsaa det reporteren har huket av fra nettflata, og det skal ut
   * av regis liste. Se `su_stilt` i db.ts for hvorfor det er en buffer.
   */
  readonly stilt: readonly string[];
}

const TOMT: SuData = { deltakere: [], spoersmaal: [], stilt: [] };

/**
 * Det som ligger lokalt. Svarer med én gang, uten nett.
 *
 * Basen maa vaere aapnet foerst. `openDb` er memoisert, saa dette venter paa
 * migreringen som alt er i gang framfor aa starte en ny — uten det spoer vi
 * etter `su_question` foer tabellen finnes.
 */
export async function lesLokalt(produksjonId: string): Promise<SuData> {
  await db.openDb(produksjonId);
  const [deltakere, spoersmaal, stilt] = await Promise.all([
    db.lastDeltakere(produksjonId),
    db.lastSpoersmaal(produksjonId),
    db.lastStilt(produksjonId),
  ]);
  return { deltakere, spoersmaal, stilt };
}

/**
 * Henter fra Supabase og skriver over det lokale.
 *
 * HELE LISTA BYTTES, ikke bare det nye. Et spoersmaal redaktoer har fjernet
 * skal forsvinne fra telefonen ogsaa — det er som regel fjernet fordi det
 * ikke skal stilles lenger, og et spoersmaal man ikke skulle stilt er verre
 * enn et som mangler.
 *
 * Returnerer null naar det ikke finnes noen klient eller hentingen feiler.
 * Appen skal kunne brukes uten konto og uten nett; da staar det lokale.
 */
export async function synkroniser(produksjonId: string): Promise<SuData | null> {
  /* Deltakerne og spoersmaalene hoerer til en produksjon. Privat har ingen,
     og `PRIVAT_ID` er ikke en UUID — spoerringen ville blitt avvist. */
  if (supabase === null || !erEkteProduksjon(produksjonId)) return null;

  const [deltakerSvar, spoersmaalSvar, stiltSvar] = await Promise.all([
    supabase
      .from("participant")
      .select("id, name, abbreviation")
      .eq("production_id", produksjonId)
      .order("abbreviation", { ascending: true }),
    supabase
      .from("su_question")
      /*
       * KILDEN HENTES MED, i samme spoerring.
       *
       * Redaktoeren skriver spoersmaalet ut av en loggpost, og klipperen skal
       * kunne finne fram innholdet. Posten staar som regel i en ANNEN
       * regissoers oekt — denne telefonen har aldri sett den — saa alt som
       * trengs for aa regne ut tidskoden maa komme ned her: postens
       * avlesning og ankeret den hoerer til. Lagt til 24.10.2026.
       */
      .select(
        "id, participant_id, text, sort_order, source_entry_id,"
        + " created_at, shoot_day,"
        + " kilde:log_entry!su_question_source_entry_id_fkey("
        + "text, monotonic_ms, boot_id, wall_utc,"
        + " oekt:log_session(shoot_day, report(version)),"
        + " anker:tc_anchor(tc_frames, framerate, drop_frame, monotonic_ms,"
        + " boot_id, wall_utc))",
      )
      .eq("production_id", produksjonId)
      .order("sort_order", { ascending: true }),
    /*
     * HVA SOM ER STILT, AV HVEM SOM HELST.
     *
     * `!inner` med `deleted_at is null` holder invarianten: et svar teller
     * bare naar posten lever. Slettes synkposten — i appen eller paa nett —
     * faller spoersmaalet tilbake paa lista.
     */
    supabase
      .from("su_answer")
      /*
       * HVEM som stilte det, og HVILKEN DAG.
       *
       * Rapporten skiller tre tilstander — stilt her, stilt av en annen,
       * ikke stilt — og den midterste trenger et navn. Brukeren
       * 03.11.2026: «saa kommer det opp at spoersmaalet er stilt av (for
       * eksempel Lars)». Navnet kommer ned her og blir liggende lokalt:
       * rapporten lages ofte uten dekning.
       */
      .select("question_id, participant_id,"
        + " post:log_entry!inner(deleted_at, author_id,"
        + " forfatter:production_member!log_entry_author_id_fkey(display_name),"
        + " oekt:log_session(shoot_day))")
      .eq("production_id", produksjonId)
      .is("post.deleted_at", null),
  ]);

  if (deltakerSvar.error !== null || spoersmaalSvar.error !== null) return null;

  const deltakere: Deltaker[] = (deltakerSvar.data ?? []).map((r) => ({
    id: String(r.id),
    navn: String(r.name),
    forkortelse: String(r.abbreviation),
  }));
  /*
   * Raden leses som et kart og ikke gjennom den genererte typen.
   *
   * Den innebygde `log_entry`-delen av spoerringen er for sammensatt for
   * typeutledningen i klienten — den gir `GenericStringError` for hele raden,
   * og da er ingen av feltene lesbare. Kartet er ærligere enn en type som
   * later som den vet.
   */
  const spoersmaal: SuSpoersmaal[] = (spoersmaalSvar.data ?? []).map((rad) => {
    const r = rad as unknown as Record<string, unknown>;
    return {
      id: String(r["id"]),
      deltakerId: r["participant_id"] == null ? null : String(r["participant_id"]),
      tekst: String(r["text"]),
      sortOrder: Number(r["sort_order"]),
      skrevetMs: r["created_at"] == null ? null : Date.parse(String(r["created_at"])),
      opptaksdag: r["shoot_day"] == null ? null : String(r["shoot_day"]),
      kilde: tilKilde(r),
    };
  });

  /* Feiler bare denne, er det bufferen som staar — ikke lista. Da er det
     bedre aa ta resten enn aa droppe alt. */
  const par = (stiltSvar.error !== null ? [] : (stiltSvar.data ?? [])).map((rad) => {
    const r = rad as unknown as Record<string, unknown>;
    const post = (r["post"] ?? {}) as Record<string, unknown>;
    const hvem = (post["forfatter"] ?? {}) as { display_name?: string };
    const oekt = (post["oekt"] ?? {}) as { shoot_day?: string };
    return {
      question: String(r["question_id"]),
      participant: String(r["participant_id"]),
      hvem: String(hvem.display_name ?? ""),
      dag: String(oekt.shoot_day ?? ""),
    };
  });

  await db.openDb(produksjonId);
  await db.erstattDeltakere(produksjonId, deltakere);
  await db.erstattSpoersmaal(produksjonId, spoersmaal);
  if (stiltSvar.error === null) await db.erstattStilt(produksjonId, par);
  return {
    deltakere,
    spoersmaal,
    stilt: par.map((x) => `${x.question}·${x.participant}`),
  };
}

/**
 * Opphavsposten, regnet om til det rapporten skal vise.
 *
 * TIDSKODEN REGNES HER, av postens EGET anker — ikke av dagens. En post fra
 * opptaksdag 6 hoerer til dag 6 sitt anker, og dagens ville gitt et tall som
 * ikke finnes noe sted i materialet.
 *
 * Rapportversjonen er den HOEYESTE som er levert for oekta. Er det levert
 * flere, er den siste den klipperen har.
 */
function tilKilde(r: Record<string, unknown>): Spoersmaalskilde | null {
  const id = r["source_entry_id"];
  const k = r["kilde"] as Record<string, unknown> | null | undefined;
  if (id === null || id === undefined || k === null || k === undefined) return null;

  const a = k["anker"] as Record<string, unknown> | null | undefined;
  const oekt = k["oekt"] as Record<string, unknown> | null | undefined;
  if (a === null || a === undefined) return null;

  const rate = String(a["framerate"]) as FramerateName;
  const dropFrame = Boolean(a["drop_frame"]);
  const spec = timecodeSpec(rate, dropFrame);
  const t = timecodeAt(
    {
      tcFrames: Number(a["tc_frames"]),
      monotonicMs: Number(a["monotonic_ms"]),
      bootId: String(a["boot_id"]),
      wallUtcMs: Date.parse(String(a["wall_utc"])),
      framerate: rate,
      dropFrame,
      source: "manual",
    },
    {
      monotonicMs: Number(k["monotonic_ms"]),
      bootId: String(k["boot_id"]),
      wallUtcMs: Date.parse(String(k["wall_utc"])),
    },
    spec,
  );

  const rapporter = (oekt?.["report"] ?? []) as readonly Record<string, unknown>[];
  const versjoner = rapporter.map((x) => Number(x["version"]));

  return {
    entryId: String(id),
    tcFrames: t.frames,
    framerate: rate,
    dropFrame,
    opptaksdag: oekt === null || oekt === undefined ? "" : String(oekt["shoot_day"] ?? ""),
    tekst: String(k["text"] ?? ""),
    rapportversjon: versjoner.length === 0 ? null : Math.max(...versjoner),
  };
}

/**
 * Legger til et spoersmaal i felt.
 *
 * Skrives LOKALT FOERST og sendes etterpaa. Regi staar i en gang med et nytt
 * spoersmaal i hodet; at telefonen ikke har dekning kan ikke bety at det blir
 * borte. Naar synkroniseringen kommer i fase 5, hentes det opp derfra.
 */
export async function leggTilSpoersmaal(
  produksjonId: string,
  deltakerId: string | null,
  tekst: string,
  sortOrder: number,
  /*
   * Opptaksdagen det skrives paa — og i appen er det DAGEN I DAG.
   *
   * De som er ute paa opptak jobber med dagen som er, og `session.shootDay`
   * er nettopp den. Nettflata er annerledes: der er det dagen som staar
   * framme i toppstripa, som kan vaere en annen. Se apps/web/src/state/su.ts.
   *
   * Knaggen peker klipperen til materialet, og et tidspunkt kan ikke svare
   * paa det: et skift som gaar over midnatt hoerer fortsatt til dagen det
   * startet.
   */
  opptaksdag: string,
  /**
   * Loggposten spoersmaalet ble skrevet UT AV, eller null.
   *
   * ========  REGI SKRIVER SYNKSPOERSMAAL FRA FELT  ========
   *
   * Brukeren 03.11.2026: «Jeg oensker at regi skal kunne skrive
   * synkspoersmaal fra appen.» Det er den samme koblingen redaktoeren
   * lager fra nettflata — `su_question.source_entry_id` — og den er verdt
   * mer herfra: regi STOD der da det skjedde, og vet hvilken hendelse
   * spoersmaalet hoerer til.
   *
   * Koblingen gjoer tre ting: spoersmaalet dukker opp paa posten i loggen
   * (gjennom `tilbakemelding`), det staar i synklista til deltakeren, og
   * rapporten kan peke tilbake til hendelsen.
   *
   * ÉN PER POST. `su_question (source_entry_id)` er unik der den er satt —
   * se migrasjon 20261021090000. Flata spoer ikke om lov; den viser
   * spoersmaalet som alt staar der i stedet for aa tilby et nytt.
   */
  kildePostId: string | null = null,
): Promise<SuSpoersmaal> {
  const q: SuSpoersmaal = {
    id: db.newId(),
    deltakerId,
    tekst,
    sortOrder,
    skrevetMs: Date.now(),
    opptaksdag,
  };
  await db.openDb(produksjonId);
  await db.lagreSpoersmaal(produksjonId, q);
  if (supabase !== null) {
    // Feiler den, staar raden lokalt. Det er den riktige halvveien.
    await supabase.from("su_question").insert({
      id: q.id,
      production_id: produksjonId,
      participant_id: deltakerId,
      text: tekst,
      sort_order: sortOrder,
      shoot_day: opptaksdag,
      source_entry_id: kildePostId,
    });
  }
  return q;
}

export { TOMT as TOM_SU };
